import { useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Link2, Loader2, Wallet } from "lucide-react";

import { PreviewBadge, comingSoon } from "@/components/admin/config/MercadoPagoSettings";
import { AdminCard } from "@/components/admin/primitives";
import { PasswordChecklist, isStrongPassword } from "@/components/PasswordChecklist";
import { errorMessage } from "@/lib/errors";
import { useChangePassword } from "@/lib/queries";
import { useResellerMe } from "@/lib/resellers";
import { useStoreSettings } from "@/lib/settings";
import { useSession } from "@/lib/session";

export const Route = createFileRoute("/revendedor/_area/dados")({
  head: () => ({ meta: [{ title: "Meus dados — Revenda MonteBox" }] }),
  component: ResellerData,
});

const input =
  "mt-1.5 h-11 w-full rounded-mb-field border border-mb-line bg-white px-4 text-sm text-mb-ink outline-none focus:border-mb-navy";

/**
 * Dados do revendedor.
 *
 * A chave Pix aparece mascarada e só a administração altera: é para onde vai
 * o dinheiro, e uma troca não conferida é o golpe mais simples que existe.
 */
function ResellerData() {
  const { profile } = useSession();
  const me = useResellerMe();
  const payment = useStoreSettings().data?.payment;
  const connected = me.data?.mercadoPagoConnected ?? false;
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    change.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          toast.success("Senha alterada.");
          setCurrent("");
          setNext("");
        },
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="font-display text-2xl font-bold tracking-tight text-mb-navy">Meus dados</h1>

      <AdminCard className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-display text-base font-bold text-mb-navy">
              <Wallet className="h-[18px] w-[18px]" />
              Recebimento pelo Mercado Pago
              <PreviewBadge />
            </h2>
            <p className="mt-1 max-w-xl text-xs leading-4 text-mb-muted text-pretty">
              Conecte a sua conta Mercado Pago para receber as comissões automaticamente — sem
              esperar o Pix manual.{" "}
              {payment
                ? `O repasse cai ${payment.payoutExtraDays} ${
                    payment.payoutExtraDays === 1 ? "dia" : "dias"
                  } depois de o Mercado Pago liberar o dinheiro da venda.`
                : ""}
            </p>
          </div>
          <span
            className={
              connected
                ? "rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800"
                : "rounded-full bg-mb-surface px-3 py-1 text-xs font-bold text-mb-muted"
            }
          >
            {connected ? "Conta conectada" : "Não conectada"}
          </span>
        </div>
        <button
          type="button"
          onClick={comingSoon}
          className="mt-4 flex items-center gap-2 rounded-mb-button bg-[#009ee3] px-4 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90"
        >
          <Link2 className="h-4 w-4" />
          {connected ? "Trocar a conta conectada" : "Conectar minha conta Mercado Pago"}
        </button>
        <p className="mt-2 text-[11px] text-mb-muted">
          A conexão é feita no site do Mercado Pago (OAuth): a MonteBox nunca vê a sua senha.
        </p>
      </AdminCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <AdminCard className="p-6">
          <h2 className="font-display text-base font-bold text-mb-navy">Conta</h2>
          <dl className="mt-4 grid grid-cols-[110px_minmax(0,1fr)] gap-y-2 text-sm [overflow-wrap:anywhere]">
            <dt className="text-mb-muted">Nome</dt>
            <dd className="text-mb-ink">{profile?.name}</dd>
            <dt className="text-mb-muted">E-mail</dt>
            <dd className="text-mb-ink">{profile?.email}</dd>
            <dt className="text-mb-muted">Nome na loja</dt>
            <dd className="text-mb-ink">{me.data?.storeName ?? "—"}</dd>
            <dt className="text-mb-muted">Chave Pix</dt>
            <dd className="text-mb-ink">{me.data?.pixKey ?? "não cadastrada"}</dd>
          </dl>
          <p className="mt-4 rounded-mb-field bg-mb-surface px-3 py-2 text-[11px] leading-4 text-mb-muted">
            Para mudar a chave Pix ou o nome na loja, fale com a administração — por segurança, os
            dados de repasse só mudam com conferência.
          </p>
        </AdminCard>

        <AdminCard className="p-6">
          <h2 className="font-display text-base font-bold text-mb-navy">Trocar senha</h2>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="block text-xs font-bold text-mb-ink">
              Senha atual
              <input
                type="password"
                value={current}
                autoComplete="current-password"
                onChange={(event) => setCurrent(event.target.value)}
                className={input}
                required
              />
            </label>
            <label className="block text-xs font-bold text-mb-ink">
              Nova senha
              <input
                type="password"
                value={next}
                autoComplete="new-password"
                onChange={(event) => setNext(event.target.value)}
                className={input}
                required
              />
            </label>
            <PasswordChecklist
              password={next}
              name={profile?.name}
              email={profile?.email}
              className="text-mb-muted"
            />
            <button
              type="submit"
              disabled={
                change.isPending ||
                !current ||
                !isStrongPassword(next, { name: profile?.name, email: profile?.email })
              }
              className="flex items-center justify-center gap-2 rounded-mb-button bg-mb-navy px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {change.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar nova senha
            </button>
          </form>
        </AdminCard>
      </div>
    </div>
  );
}
